import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import ItemCardCst from "@components/ItemCardCst";
import { Stakeholder } from "@components/StakeholdersCst";
import StakeholdersPickerCst from "@components/StakeholdersPickerCst";
import { useWizardReadOnly } from "@components/WizardCst";

interface StakeholdersFieldCstProps {
  title: string;
  description?: string;
  addLabel: string;
  values: Stakeholder[];
  onChange: (next: Stakeholder[]) => void;
  disabled?: boolean;
}

const StakeholdersFieldCst: React.FC<StakeholdersFieldCstProps> = ({
  title,
  description,
  addLabel,
  values,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("stakeholders-picker");
  const readOnly = useWizardReadOnly();
  const isDisabled = disabled || readOnly;

  const [pickerOpen, setPickerOpen] = useState(false);

  const excludeUserIds = values
    .map((s) => s.userId)
    .filter((id): id is number => typeof id === "number");

  const openPicker = () => {
    if (isDisabled) return;
    setPickerOpen(true);
  };

  const handleConfirm = (stakeholder: Stakeholder) => {
    onChange([...values, stakeholder]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <Box
            onClick={openPicker}
            aria-disabled={isDisabled}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: isDisabled ? "not-allowed" : "pointer",
              color: isDisabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
              fontSize: "13px",
              fontWeight: 600,
              "&:hover": isDisabled ? {} : { textDecoration: "underline" },
            }}
          >
            {addLabel}
            <Plus size={14} />
          </Box>
        </Box>
        {description && (
          <Typography
            sx={{
              fontSize: "14px",
              color: theme.palette.text.primary,
              mt: 0.5,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {values.map((stakeholder, idx) => (
          <ItemCardCst
            key={`stkhldr_${stakeholder.userId}_${idx}`}
            title={stakeholder.name || `#${stakeholder.userId}`}
            segments={[
              stakeholder.type,
              stakeholder.visStakeholder
                ? t("visible-to-learner")
                : t("not-visible-to-learner"),
            ]}
            onDelete={isDisabled ? undefined : () => handleRemove(idx)}
          />
        ))}

        <EmptyAddCardCst
          addLabel={addLabel}
          onAdd={openPicker}
          disabled={isDisabled}
          minHeight={values.length > 0 ? "64px" : "120px"}
        />
      </Box>

      <StakeholdersPickerCst
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t("title") || "Aggiungi Stakeholder"}
        excludeUserIds={excludeUserIds}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};

export default StakeholdersFieldCst;
